import { hashCode } from './string';

const isJSONPrimitive = (val: any): boolean =>
  val === null ||
  val === Infinity ||
  typeof val === 'boolean' ||
  typeof val === 'number' ||
  typeof val === 'string' ||
  val instanceof Boolean ||
  val instanceof Number ||
  val instanceof String ||
  val.toJSON != null;

/**
 * Stably stringifies any value that can be serialized using JSON.stringify.
 * Object keys are sorted so that objects that are deeply equal will serialize
 * to the same string, regardless of how each object was constructed.
 */
export const stableStringify = (val: any): string => {
  if (val !== undefined && isJSONPrimitive(val)) {
    return `${JSON.stringify(val)}`;
  } else if (Array.isArray(val)) {
    return `[${val.map(e => stableStringify(e)).join(',')}]`;
  } else if (typeof val === 'object') {
    return `{${Object.keys(val)
      .sort()
      .filter(k => val[k] !== undefined)
      .map(k => `${JSON.stringify(k)}:${stableStringify(val[k])}`)
      .join(',')}}`;
  } else {
    throw new Error(`Cannot hash object: unsupported value '${val}' of type '${typeof val}'`);
  }
};

/**
 * Produces a 32 bit integer that can be used as a content addressable
 * identifier for objects. Objects are treated as unordered sets of keys and
 * values, so two objects that pass a deep comparison will receive the same
 * hash, regardless of the order in which the keys were added. Only accepts
 * values that can be serialized using JSON.stringify.
 */
export const hashObject = (obj: any): number => hashCode(stableStringify(obj));
